import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  toast,
} from "@devhop/ui";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArrowLeftIcon,
  Building2,
  CheckCircle2,
  ClipboardList,
  EditIcon,
  FileTextIcon,
  HelpCircle,
  PlusIcon,
  Target,
  Users,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import type { KPIAssignmentListItem } from "../../domain/types";
import { useChecklistMappingsByKpi } from "@/modules/checklist/presentation/api/queries";
import {
  useCreateKPIAssignment,
  useDeleteKPIAssignment,
  useKPIAssignmentImpactQuery,
  useKPIAssignmentsQuery,
  useKPIQuery,
  useUpdateKPIAssignment,
} from "../api/queries";
import { KPIDetailTour, useKPIDetailTour } from "../tour/KPIDetailTour";
import { KPIAssignmentFilter } from "../ui/KPIAssignmentFilter";
import { KPIAssignmentForm } from "../ui/KPIAssignmentForm";
import { KPIAssignmentTable } from "../ui/KPIAssignmentTable";
import {
  getKpiLevelLabel,
  KPI_LEVEL_BADGE_VARIANT,
  type KpiLevel,
} from "../utils/kpi-level-labels";

export function KPIDetailPage() {
  const tour = useKPIDetailTour();
  const nav = useNavigate({ from: "/app/kpi/$id" });
  const { id } = useParams({ from: "/app/kpi/$id" });
  const search = useSearch({ from: "/app/kpi/$id" }) as OffsetPageQueryDTO & {
    assignmentOffset?: number;
    assignmentLimit?: number;
  };

  const kpiDetail = useKPIQuery(id);
  const impactQuery = useKPIAssignmentImpactQuery(id);
  const checklistMappingsForKpi = useChecklistMappingsByKpi(id ?? "");

  const [assignmentFilters, setAssignmentFilters] = useState<{
    assignmentType?: "employee" | "position" | "department";
    isActive?: boolean;
    departmentId?: string;
  }>({});

  const assignmentsQuery = useKPIAssignmentsQuery({
    kpiId: id,
    offset: search.assignmentOffset ?? 0,
    limit: search.assignmentLimit ?? 20,
    sort: search.sort,
    filters: search.filters,
    ...assignmentFilters,
  });

  const canUpdate = useActionPermission(["kpi:update"]);
  const canManage = useActionPermission(["kpi:update", "kpi:delete"]);
  const canCreate = useActionPermission(["kpi:update"]);

  const createAssignment = useCreateKPIAssignment();
  const updateAssignment = useUpdateKPIAssignment();
  const deleteAssignment = useDeleteKPIAssignment();

  const createModal = useDisclosure();
  const editModal = useDisclosure<KPIAssignmentListItem>();

  const getLevelBadge = (level: string) => {
    const key = level as KpiLevel;
    const variant = KPI_LEVEL_BADGE_VARIANT[key] ?? "default";
    return (
      <Badge variant={variant} className="text-xs">
        {getKpiLevelLabel(level)}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const config = {
      active: { label: "ກຳລັງໃຊ້ງານ", variant: "default" as const },
      draft: { label: "ຮ່າງ", variant: "secondary" as const },
      inactive: { label: "ປິດການໃຊ້ງານ", variant: "outline" as const },
    };
    const cfg = config[status as keyof typeof config] || config.draft;
    return (
      <Badge variant={cfg.variant} className="text-xs">
        {cfg.label}
      </Badge>
    );
  };

  const getPeriodLabel = (period: string) => {
    const config = {
      monthly: "ເດືອນ",
      quarterly: "ໄຕມາດ",
      yearly: "ປີ",
    };
    return config[period as keyof typeof config] || period;
  };

  return (
    <>
      <Header />
      <Main>
        <KPIDetailTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <div className="space-y-4">
          <div
            className="flex items-center justify-between gap-4"
            data-tourid="detail-header"
          >
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ລາຍລະອຽດ KPI</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                ຂໍ້ມູນລາຍລະອຽດຂອງ KPI
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => nav({ to: `/app/kpi/${id}/pdf-preview` })}
              >
                <FileTextIcon className="mr-2 h-4 w-4" />
                ສົ່ງອອກ PDF
              </Button>
              {canUpdate && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => nav({ to: `/app/kpi/${id}/edit` })}
                >
                  <EditIcon className="mr-2 h-4 w-4" />
                  ແກ້ໄຂ
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => nav({ to: "/app/kpis" })}
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          <QueryState
            result={kpiDetail}
            loadingFallback={<div className="p-4 text-center">ກຳລັງໂຫຼດ...</div>}
            errorFallback={(error) => (
              <div className="p-4 text-center text-destructive">
                ບໍ່ພົບຂໍ້ມູນ KPI: {error.message}
              </div>
            )}
          >
            {kpiDetail.data && (
              <div className="space-y-6">
                {/* ຂໍ້ມູນພື້ນຖານ */}
                <Card data-tourid="detail-basic-info">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">ຂໍ້ມູນພື້ນຖານ</CardTitle>
                      <div className="flex items-center gap-2">
                        {getLevelBadge(kpiDetail.data.level)}
                        {getStatusBadge(kpiDetail.data.status)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="md:col-span-2">
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຊື່ KPI
                        </div>
                        <p className="mt-1 text-sm">{kpiDetail.data.name}</p>
                      </div>
                      {kpiDetail.data.description && (
                        <div>
                          <div className="font-medium text-muted-foreground text-sm">
                            ຄຳອະທິບາຍ (ວັດແທກແນວໃດ)
                          </div>
                          <p className="mt-1 whitespace-pre-wrap text-sm">
                            {kpiDetail.data.description}
                          </p>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">
                        ລະດັບ
                      </div>
                      <p className="mt-1 text-sm">
                        {getLevelBadge(kpiDetail.data.level)}
                      </p>
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">
                        ສະຖານະ
                      </div>
                      <p className="mt-1 text-sm">
                        {getStatusBadge(kpiDetail.data.status)}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* ວິທີການວັດ */}
                <Card data-tourid="detail-measurement">
                  <CardHeader>
                    <CardTitle className="text-lg">ວິທີການວັດ</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">
                        ນັບແນວໃດ (ວິທີການວັດ)
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm">
                        {kpiDetail.data.measurementMethod}
                      </p>
                    </div>
                    {kpiDetail.data.measurementSource && (
                      <div className="md:order-3">
                        <div className="font-medium text-muted-foreground text-sm">
                          ວັດຈາກຫຍັງ (ແຫຼ່ງຂໍ້ມູນ)
                        </div>
                        <p className="mt-1 text-sm">
                          {kpiDetail.data.measurementSource}
                        </p>
                      </div>
                    )}
                    <div className="md:order-2">
                      <div className="font-medium text-muted-foreground text-sm">
                        ຮອບການວັດ
                      </div>
                      <p className="mt-1 text-sm">
                        {getPeriodLabel(kpiDetail.data.measurementPeriod)}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* ເປົ້າໝາຍ */}
                <Card data-tourid="detail-target">
                  <CardHeader>
                    <CardTitle className="text-lg">ເປົ້າໝາຍ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      {/* ຄ່າເປົ້າໝາຍ - เน้น */}
                      {kpiDetail.data.targetValue != null && (
                        <div className="md:col-span-3">
                          <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-4">
                            <div className="rounded-full bg-primary/10 p-2">
                              <Target className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-muted-foreground text-xs">
                                ຄ່າເປົ້າໝາຍ
                              </div>
                              <p className="mt-1 font-semibold text-lg">
                                {typeof kpiDetail.data.targetValue === "number"
                                  ? kpiDetail.data.targetValue.toLocaleString()
                                  : Number(
                                      kpiDetail.data.targetValue || 0,
                                    ).toLocaleString()}{" "}
                                {kpiDetail.data.targetUnit === "percent"
                                  ? "%"
                                  : kpiDetail.data.targetUnit === "count"
                                    ? "ຄັ້ງ"
                                    : kpiDetail.data.targetUnit === "score"
                                      ? "ຄະແນນ"
                                      : kpiDetail.data.targetUnit === "currency"
                                        ? "ກີບ"
                                        : ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ເກນຜ່ານ */}
                      {kpiDetail.data.passCriteria && (
                        <div>
                          <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50/50 p-4 dark:border-green-900/30 dark:bg-green-950/20">
                            <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600 dark:text-green-500" />
                            <div className="flex-1">
                              <div className="font-medium text-green-700 text-sm dark:text-green-400">
                                ເກນຜ່ານ
                              </div>
                              <p className="mt-1.5 text-sm">
                                {kpiDetail.data.passCriteria}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ເກນບໍ່ຜ່ານ */}
                      {kpiDetail.data.failCriteria && (
                        <div>
                          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-900/30 dark:bg-red-950/20">
                            <XCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-500" />
                            <div className="flex-1">
                              <div className="font-medium text-red-700 text-sm dark:text-red-400">
                                ເກນບໍ່ຜ່ານ
                              </div>
                              <p className="mt-1.5 text-sm">
                                {kpiDetail.data.failCriteria}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* ໝາຍເຫດ */}
                {kpiDetail.data.notes && (
                  <Card data-tourid="detail-notes">
                    <CardHeader>
                      <CardTitle className="text-lg">ໝາຍເຫດ</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="whitespace-pre-wrap text-sm">
                        {kpiDetail.data.notes}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* ດຶງຄ່າຈາກ Checklist (Phase 4: Checklist → KPI) */}
                {checklistMappingsForKpi.data?.data &&
                  checklistMappingsForKpi.data.data.length > 0 && (
                    <Card data-tourid="detail-checklist-source">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <ClipboardList className="h-5 w-5" />
                          ດຶງຄ່າຈາກ Checklist
                        </CardTitle>
                        <p className="text-muted-foreground text-sm">
                          KPI ນີ້ຖືກອັບເດດອັດຕະໂນມັດຈາກການເຮັດ checklist ຕໍ່ໄປນີ້
                        </p>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {checklistMappingsForKpi.data.data.map((m) => (
                            <li key={m.id}>
                              <Link
                                to="/app/checklists/$id"
                                params={{ id: m.checklistId }}
                                className="text-sm font-medium text-primary hover:underline"
                              >
                                {m.checklistTitle}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                {/* ສະຖິຕິການມອບໝາຍ */}
                {impactQuery.data && (
                  <div
                    data-tourid="detail-assignment-stats"
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                  >
                    {/* ຈຳນວນການມອບໝາຍທັງໝົດ */}
                    <div className="rounded-lg border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <div className="rounded-md bg-blue-100 p-2 dark:bg-blue-900/30">
                          <ClipboardList className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="font-medium text-muted-foreground text-xs">
                          ຈຳນວນການມອບໝາຍທັງໝົດ
                        </div>
                      </div>
                      <p className="font-semibold text-2xl">
                        {impactQuery.data.assignmentCount}
                      </p>
                    </div>

                    {/* ກຳລັງໃຊ້ງານ */}
                    <div className="rounded-lg border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <div className="rounded-md bg-green-100 p-2 dark:bg-green-900/30">
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="font-medium text-muted-foreground text-xs">
                          ກຳລັງໃຊ້ງານ
                        </div>
                      </div>
                      <p className="font-semibold text-2xl">
                        {impactQuery.data.activeAssignmentCount}
                      </p>
                    </div>

                    {/* ພະນັກງານ */}
                    <div className="rounded-lg border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <div className="rounded-md bg-purple-100 p-2 dark:bg-purple-900/30">
                          <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="font-medium text-muted-foreground text-xs">
                          ພະນັກງານ
                        </div>
                      </div>
                      <p className="font-semibold text-2xl">
                        {impactQuery.data.affectedEmployees.length}
                      </p>
                    </div>

                    {/* ຕໍາແໜ່ງ/ພະແນກ */}
                    <div className="rounded-lg border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <div className="rounded-md bg-orange-100 p-2 dark:bg-orange-900/30">
                          <Building2 className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="font-medium text-muted-foreground text-xs">
                          ຕໍາແໜ່ງ/ພະແນກ
                        </div>
                      </div>
                      <p className="font-semibold text-2xl">
                        {impactQuery.data.affectedPositions.length +
                          impactQuery.data.affectedDepartments.length}
                      </p>
                    </div>
                  </div>
                )}

                {/* ການມອບໝາຍ */}
                <Card data-tourid="detail-assignments">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">ການມອບໝາຍ</CardTitle>
                      {canCreate && (
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={createModal.open}
                        >
                          <PlusIcon className="mr-2 h-4 w-4" />
                          ເພີ່ມການມອບໝາຍ
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <KPIAssignmentFilter
                        kpiId={id}
                        hideKpiFilter={true}
                        assignmentType={
                          assignmentFilters.assignmentType || "all"
                        }
                        isActive={assignmentFilters.isActive ?? "all"}
                        departmentId={assignmentFilters.departmentId}
                        onFilterChange={(newFilters) => {
                          setAssignmentFilters({
                            assignmentType: newFilters.assignmentType,
                            isActive: newFilters.isActive,
                            departmentId: newFilters.departmentId || undefined,
                          });
                        }}
                      />
                    </div>
                    {assignmentsQuery.isLoading ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        ກຳລັງໂຫຼດ...
                      </div>
                    ) : assignmentsQuery.data?.data.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        ຍັງບໍ່ມີການມອບໝາຍ
                      </div>
                    ) : assignmentsQuery.data?.data ? (
                      <KPIAssignmentTable
                        canManage={!!canManage}
                        isLoading={assignmentsQuery.isLoading}
                        data={assignmentsQuery.data?.data ?? []}
                        offset={search.assignmentOffset ?? 0}
                        limit={search.assignmentLimit ?? 20}
                        totalCount={assignmentsQuery.data?.meta?.total ?? 0}
                        sortBy={search.sort ? search.sort[0]?.field : undefined}
                        sortOrder={
                          search.sort
                            ? (search.sort[0]?.dir as "asc" | "desc")
                            : undefined
                        }
                        onPaginationChange={(offset, limit) => {
                          nav({
                            search: {
                              ...search,
                              assignmentOffset: offset,
                              assignmentLimit: limit,
                            },
                          });
                        }}
                        onSortingChange={(id, desc) => {
                          nav({
                            search: {
                              ...search,
                              sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                            },
                          });
                        }}
                        onEdit={(assignment) => editModal.openWith(assignment)}
                        onDelete={async (assignmentId) => {
                          await toast.promise(
                            deleteAssignment
                              .mutateAsync(assignmentId)
                              .then((result) => {
                                if (result?.hasResults) {
                                  setTimeout(() => {
                                    toast.warning(
                                      "ການມອບໝາຍນີ້ມີຜົນການປະເມີນແລ້ວ ແຕ່ໄດ້ຖືກລຶບແລ້ວ",
                                    );
                                  }, 100);
                                }
                                return result;
                              }),
                            {
                              loading: "ກຳລັງລຶບ...",
                              success: "ລຶບການມອບໝາຍສຳເລັດ",
                              error: "ລຶບການມອບໝາຍລົ້ມເຫຼວ",
                            },
                          );
                        }}
                      />
                    ) : null}
                  </CardContent>
                </Card>

                {/* ຂໍ້ມູນອື່ນໆ */}
                <Card data-tourid="detail-additional-info">
                  <CardHeader>
                    <CardTitle className="text-lg">ຂໍ້ມູນອື່ນໆ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <div className="font-medium text-muted-foreground text-xs">
                          ວັນທີສ້າງ
                        </div>
                        <p className="mt-1 text-sm">
                          {kpiDetail.data.createdAt
                            ? format(
                                new Date(kpiDetail.data.createdAt),
                                "dd/MM/yyyy HH:mm",
                              )
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <div className="font-medium text-muted-foreground text-xs">
                          ວັນທີແກ້ໄຂລ່າສຸດ
                        </div>
                        <p className="mt-1 text-sm">
                          {kpiDetail.data.updatedAt
                            ? format(
                                new Date(kpiDetail.data.updatedAt),
                                "dd/MM/yyyy HH:mm",
                              )
                            : "-"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </QueryState>
        </div>
      </Main>

      {/* Create Assignment Modal */}
      <Modal
        open={!!createModal.isOpen}
        onOpenChange={createModal.toggle}
        title="ເພີ່ມການມອບໝາຍ KPI"
        description="ຜູກ KPI ກັບຕໍາແໜ່ງ, ພະນັກງານ, ຫຼືພະແນກ"
        size="lg"
      >
        <KPIAssignmentForm
          initialKpiId={id}
          onSubmit={async (vals) => {
            await createAssignment.mutateAsync(
              vals as Parameters<typeof createAssignment.mutateAsync>[0],
            );
            createModal.close();
          }}
          submitting={createAssignment.isPending}
          isEdit={false}
        />
      </Modal>

      {/* Edit Assignment Modal */}
      <Modal
        open={!!editModal.isOpen}
        onOpenChange={editModal.toggle}
        title="ແກ້ໄຂການມອບໝາຍ KPI"
        description="ປັບປຸງການມອບໝາຍ KPI"
        size="lg"
      >
        {editModal.data && (
          <KPIAssignmentForm
            initialKpiId={id}
            updateInitialValues={{
              startDate: editModal.data.startDate
                ? editModal.data.startDate.split("T")[0]
                : undefined,
              endDate: editModal.data.endDate
                ? editModal.data.endDate.split("T")[0]
                : undefined,
              isActive: editModal.data.isActive,
            }}
            onSubmit={async (vals) => {
              if (!editModal.data?.id) return;
              await updateAssignment.mutateAsync({
                id: editModal.data.id,
                input: vals,
              });
              editModal.close();
            }}
            submitting={updateAssignment.isPending}
            isEdit={true}
          />
        )}
      </Modal>
    </>
  );
}
